// Screening questions come from /api/public/jobs/<slug>/ and are set up per
// role in the Django admin. Every type the model allows is supported:
// boolean (Yes/No), single_choice, multiple_choice, numeric,
// experience_years and text. A conditional question shows only when its
// parent's answer matches: a Yes/No parent uses show_when_parent_answer (a
// string such as "['Yes']"), a single-choice parent uses
// show_when_parent_options (option ids).

const UNIT = /\s*\((?:in\s+)?([^)]{1,12})\)\s*\??\s*$/i;
const CURRENCY = /^(aed|usd|eur|gbp|inr|pkr|php|lkr|sar)$/i;
const TEXT_MIN = 2;
export const TEXT_MAX = 1000;
const YEARS_MAX = 60;

// Sensible ceilings per unit, so a typo like 3000 days is caught.
const UNIT_MAX = { day: 365, days: 365, week: 52, weeks: 52, month: 120, months: 120, year: 60, years: 60 };
const NUMBER_MAX = 100000000;

export const answerKey = (id) => `q${id}`;

export const sortQuestions = (questions = []) =>
  [...questions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

// "What is your notice period? (in days)" -> label without the unit, "days".
export function splitUnit(text = "") {
  const match = UNIT.exec(text);
  if (!match) return { label: text, unit: null };
  const label = text.slice(0, match.index).trim();
  const needsMark = text.includes("?") && !label.endsWith("?");
  return { label: needsMark ? `${label}?` : label, unit: match[1].trim() };
}

export const isCurrency = (unit) => CURRENCY.test(unit || "");

export const unitOf = (question) =>
  splitUnit(question.text).unit || (question.question_type === "experience_years" ? "years" : null);

const booleanParentMatches = (question, parentAnswer) => {
  const wanted = String(question.show_when_parent_answer || "");
  if (parentAnswer === true && wanted.includes("Yes")) return true;
  if (parentAnswer === false && wanted.includes("No")) return true;
  return false;
};

export function isVisible(question, questions, answers, depth = 0) {
  if (!question.is_conditional) return true;
  // Admin-entered data: a parent loop must not hang the form.
  if (depth > 10) return false;
  const parent = questions.find((q) => q.id === question.parent_question_id);
  if (!parent || !isVisible(parent, questions, answers, depth + 1)) return false;

  const parentAnswer = answers[answerKey(parent.id)];
  if (parent.question_type === "boolean") return booleanParentMatches(question, parentAnswer);
  if (parent.question_type === "single_choice") {
    return (question.show_when_parent_options || []).includes(parentAnswer);
  }
  return false;
}

export const isAnswered = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") return value.trim() !== "";
  return value !== null && value !== undefined;
};

export const visibleQuestions = (questions, answers) =>
  questions.filter((q) => isVisible(q, questions, answers));

const MISSING = {
  boolean: "Choose Yes or No",
  single_choice: "Choose one option",
  multiple_choice: "Choose at least one option",
  numeric: "Enter a number",
  experience_years: "Enter your years of experience",
  text: "Write your answer",
};

function numberProblem(question, value) {
  const raw = String(value).trim();
  if (question.question_type === "experience_years") {
    if (!/^\d{1,2}(\.\d)?$/.test(raw)) return "Use a number of years, like 2 or 1.5";
    return Number(raw) > YEARS_MAX ? `Enter ${YEARS_MAX} years or fewer` : null;
  }
  if (!/^\d+$/.test(raw)) return "Use whole numbers only, like 30";
  const unit = (unitOf(question) || "").toLowerCase();
  const max = UNIT_MAX[unit] ?? NUMBER_MAX;
  return Number(raw) > max ? `Enter a number up to ${max.toLocaleString("en-US")}` : null;
}

function textProblem(value) {
  const length = String(value).trim().length;
  if (length < TEXT_MIN) return "Write a little more";
  return length > TEXT_MAX ? `Keep it under ${TEXT_MAX} characters` : null;
}

// The message to show for one answer, or null when it's fine.
export function answerProblem(question, value) {
  if (!isAnswered(value)) {
    return question.is_required ? MISSING[question.question_type] ?? "Answer this question" : null;
  }
  if (question.question_type === "numeric" || question.question_type === "experience_years") {
    return numberProblem(question, value);
  }
  if (question.question_type === "text") return textProblem(value);
  return null;
}

// { [questionId]: message } for every visible question with a problem.
export function questionProblems(questions, answers) {
  return visibleQuestions(questions, answers).reduce((problems, q) => {
    const problem = answerProblem(q, answers[answerKey(q.id)]);
    return problem ? { ...problems, [q.id]: problem } : problems;
  }, {});
}

const toAnswerText = (question, value) => {
  switch (question.question_type) {
    case "multiple_choice": {
      const chosen = new Set(value);
      return (question.options || [])
        .filter((o) => chosen.has(o.id))
        .map((o) => o.text)
        .join(", ");
    }
    case "numeric":
      return Number(value);
    case "experience_years":
      return String(Number(value));
    case "text":
      return String(value).trim();
    default:
      return value;
  }
};

// Only questions the applicant could see, and only the ones they answered.
export const buildAnswerPayload = (questions, answers) =>
  visibleQuestions(questions, answers)
    .filter((q) => isAnswered(answers[answerKey(q.id)]))
    .map((q) => ({
      question_id: q.id,
      answer_text: toAnswerText(q, answers[answerKey(q.id)]),
    }));

export function formatNumber(value, unit) {
  if (!isAnswered(value)) return "";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  const shown = isCurrency(unit) ? n.toLocaleString("en-US") : String(value);
  return unit ? `${shown} ${unit}` : shown;
}

export function displayAnswer(question, value) {
  if (!isAnswered(value)) return "Not answered";
  switch (question.question_type) {
    case "boolean":
      return value ? "Yes" : "No";
    case "single_choice":
      return question.options?.find((o) => o.id === value)?.text ?? "";
    case "multiple_choice":
      return toAnswerText(question, value);
    case "numeric":
    case "experience_years":
      return formatNumber(value, unitOf(question));
    default:
      return String(value).trim();
  }
}
